import { Sprite, type Texture } from "pixi.js";
import type { Log, PresentationTaskRunner } from "@slot/presentation";
import { COIN_SHINE_TUNING } from "../tuning/featureTuning";
import { ART_SCALE, COIN_IDLE_LAYOUT } from "./layout";

/**
 * The artist's idle coin (`coin_idle.riv`'s `Coin`, played as its Spine conversion): the board's coin with an
 * always-on shine (its glow pulsing, now and then the whole coin catching the light, soft gold reflections turning on its
 * face, a light arc on its rim) on a seamless 20 s loop; its number `variant` (0 to 3) starts the loop a quarter later.
 */
export const COIN_IDLE_RIVE = { variant: "variant" } as const;

/** What a shine asks of its art (`SpineArtboard`: the Rive file's conversion, drawn into a texture of its own). */
export interface CoinShineArt {
  readonly texture: Texture;
  resize(scale: number): void;
  setNumber(name: string, value: number): void;
  advance(deltaSeconds: number): boolean;
  render(deltaSeconds: number): void;
  dispose(): void;
}

/** One coin's shine: a sprite of its variant's coin, centred, at the artboard's size in design units. */
export interface CoinShine {
  readonly sprite: Sprite;
  readonly variant: number;
}

interface Variant {
  readonly art: CoinShineArt;
  /** The coins showing it now. */
  users: number;
}

/**
 * The plain coins' shine (the artist's handoff, 2026-10-13): one artboard per variant (the file loaded once), each drawn on
 * the UI's runner at the device's loop rate while any coin shows it, every plain coin on the board a sprite of its
 * variant's texture: four renders a frame however many coins are out. A coin takes the next variant as it starts showing a
 * coin (`CoinSymbolView`), so neighbours landing in turn shine apart, and gives it back as its cell shows another symbol
 * or is put back. Nothing shines before they are in: the coins keep their still image.
 */
export class CoinShines {
  private readonly ambientRunner: PresentationTaskRunner;
  private readonly log: Log;
  private variants: Variant[] | null = null;
  private next = 0;
  private drawing = false;
  private disposed = false;
  /** Device pixels per design unit where the coins are drawn (the textures' size follows it). */
  private pixelsPerUnit = 1;

  public constructor(input: { ambientRunner: PresentationTaskRunner; log: Log }) {
    this.ambientRunner = input.ambientRunner;
    this.log = input.log;
  }

  /** The artist's coins, one per variant (`arts[i]` plays variant `i`), loaded. */
  public setArt(arts: readonly CoinShineArt[]): void {
    if (this.disposed || this.variants) {
      for (const art of arts) art.dispose();
      return;
    }
    this.variants = arts.map((art, variant) => {
      art.setNumber(COIN_IDLE_RIVE.variant, variant);
      art.resize(this.textureScale());
      return { art, users: 0 };
    });
    this.log("[CoinShines] the coins' shine is in");
  }

  /** Whether they are in, how many coins shine now, and how many variants are drawn (the dev hook's read). */
  public get state(): { loaded: boolean; coins: number; drawn: number } {
    const variants = this.variants ?? [];
    return {
      loaded: this.variants !== null,
      coins: variants.reduce((sum, one) => sum + one.users, 0),
      drawn: variants.filter((one) => one.users > 0).length,
    };
  }

  /** Device pixels per design unit where the coins are drawn: the textures are drawn at their size on the screen. */
  public setPixelsPerUnit(pixelsPerUnit: number): void {
    if (!(pixelsPerUnit > 0) || pixelsPerUnit === this.pixelsPerUnit) return;
    this.pixelsPerUnit = pixelsPerUnit;
    for (const one of this.variants ?? []) one.art.resize(this.textureScale());
  }

  /** A shine for a coin starting to show: the next variant's; null before they are in. */
  public take(): CoinShine | null {
    const variants = this.variants;
    if (!variants || variants.length === 0 || this.disposed) return null;
    const variant = this.next % variants.length;
    this.next = (this.next + 1) % variants.length;
    const chosen = variants[variant]!;
    chosen.users += 1;
    if (chosen.users === 1) chosen.art.render(0);
    const { width, height } = COIN_IDLE_LAYOUT.artboard;
    const sprite = new Sprite({ texture: chosen.art.texture, anchor: 0.5, label: "CoinShine" });
    sprite.setSize(width * ART_SCALE, height * ART_SCALE);
    this.draw();
    return { sprite, variant };
  }

  /** `shine` back: its sprite gone, its variant drawn no more once no coin shows it. */
  public giveBack(shine: CoinShine): void {
    const one = this.variants?.[shine.variant];
    if (one) one.users = Math.max(0, one.users - 1);
    shine.sprite.destroy();
  }

  public dispose(): void {
    this.disposed = true;
    const variants = this.variants;
    this.variants = null;
    for (const one of variants ?? []) one.art.dispose();
  }

  /** Texture pixels per artboard pixel: the coin's size on the screen, at the tuning's share. */
  private textureScale(): number {
    return ART_SCALE * this.pixelsPerUnit * COIN_SHINE_TUNING.resolution;
  }

  /** The variants some coin shows, drawn at their own pace, as long as any is shown. */
  private draw(): void {
    if (this.drawing) return;
    this.drawing = true;
    let last = 0;
    const shown = (): boolean => this.variants?.some((one) => one.users > 0) ?? false;
    void this.ambientRunner
      .tickInScope(
        "",
        (elapsed) => {
          const delta = elapsed - last;
          last = elapsed;
          for (const one of this.variants ?? []) if (one.users > 0) one.art.advance(delta);
        },
        () => this.disposed || !shown(),
      )
      .then(() => {
        this.drawing = false;
        // One shown as the last went.
        if (!this.disposed && shown()) this.draw();
      });
  }
}
